package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.shared.PluralCategory
import dev.lingvo.site.ds.Motion
import dev.lingvo.site.ds.noTransition
import dev.lingvo.site.ds.transition
import dev.lingvo.site.ds.transitionSurface
import dev.lingvo.site.i18n.t
import org.jetbrains.compose.web.dom.Div

/**
 * Presence is supplied by [EditorComposition], which owns the controller state. The present default
 * keeps isolated component previews and tests deterministic without creating a second lifecycle.
 */
internal val LocalEditorUtilityPresence = compositionLocalOf {
    EditorUtilityPresenceState.legacyPresent()
}

@Composable
internal fun EditorUtilityShellFrame(
    shell: EditorUtilityShell,
    onDismiss: () -> Unit,
    labelledBy: String? = null,
    reducedMotion: Boolean = false,
    content: @Composable () -> Unit,
) {
    val presence = LocalEditorUtilityPresence.current
    val utilityLabel = t("editor.editor_utility_presentation.translation_utility", "Translation utility")
    val phase = presence.phase
    val dormant = phase == EditorUtilityPresencePhase.PENDING_ENTER ||
        phase == EditorUtilityPresencePhase.EXITING
    val instant = presence.motionPolicy == EditorUtilityMotionPolicy.INSTANT || reducedMotion

    fun Modifier.surfaceAttributes(shellName: String): Modifier = this
        .attr("id", EDITOR_UTILITY_SURFACE_ID)
        .attr("data-editor-utility", "true")
        .attr("data-editor-utility-shell", shellName)
        .attr("data-presence", phase.attributeValue)
        .attr("data-presence-generation", presence.generation.toString())

    fun Modifier.visibleMotion(compact: Boolean): Modifier {
        val entering = phase == EditorUtilityPresencePhase.ENTERING ||
            phase == EditorUtilityPresencePhase.PRESENT
        val base = styleModifier {
            // A sheet slides in at full opacity and fades only on the way out — `LSheetMotion` is
            // explicit about that asymmetry, and it is why a sheet reads as arriving rather than
            // materialising. A sidecar is a popover and fades both ways.
            val arrivingSheet = compact && phase == EditorUtilityPresencePhase.PENDING_ENTER
            property("opacity", if (entering || arrivingSheet) "1" else "0")
            property(
                "transform",
                // **A sheet travels its own height; a sidecar travels a popover's six pixels.**
                //
                // The compact shell is a fixed, scrimmed, edge-anchored bottom sheet, and it was
                // animated with the popover tokens — 6px and a 0.97 scale. At that distance a
                // full-width panel does not read as arriving from the edge, it reads as the page
                // flinching. `LSheetMotion` is this system's ruling on exactly this case, made
                // when the mobile navigation drawer had the same six pixels.
                when {
                    entering -> "none"
                    compact -> "translateY(100%)"
                    else -> "translateX(${Motion.PopoverTravelPx}px)"
                },
            )
            if (dormant) property("pointer-events", "none")
            // **`flex-basis`, not `width`.** `EditorWorkbenchUtilityStyle`/`EditorWorkbenchTrioStyle`
            // are flex rows (see the comment on the former for why grid's `auto` track sizing
            // could not do this): the editor's sibling pane is `flex: 1 1 auto`, and a flex
            // container redistributes that free space from a fixed-basis child's *current* basis
            // on every layout pass — which is what makes this animate, unlike a `width` on a grid
            // item, whose surrounding track only ever saw the item's specified end value. Setting
            // `width` here too would do nothing under a flex parent (a non-`auto` `flex-basis`
            // wins the sizing question outright) and nothing under the class's own literal
            // `flex: 0 0 $EDITOR_UTILITY_TRACK` unless overridden with equal specificity, so both
            // go inline together.
            if (!compact) {
                val track = if (entering) EDITOR_UTILITY_TRACK else "0px"
                property("flex", "0 0 $track")
                property("width", track)
            }
        }
        // **`PENDING_ENTER` snaps; it never animates into being collapsed.**
        //
        // This element's box now persists across a remeasure (see the `when (shell)` comment
        // below), so `PENDING_ENTER`'s own style change — measurement's fixed, open width folding
        // down to 0 — is a real, painted frame for the first time, and it started competing with
        // the entrance it precedes: measured on the real export, the sidecar visibly narrowed
        // past its resting collapsed width before growing back out, because the fold-down carried
        // the *same* 180ms transition the *entrance* declares, and the two overlapped when
        // `ENTERING` began before the fold-down finished. Nobody is meant to see the fold at all —
        // `PENDING_ENTER` is `opacity: 0`, and on the sidecar the width is what the reader should
        // see change, exactly once, on the way in.
        val pendingEnter = phase == EditorUtilityPresencePhase.PENDING_ENTER
        return if (instant || pendingEnter) {
            base.noTransition()
        } else {
            val duration = when {
                phase == EditorUtilityPresencePhase.EXITING -> Motion.OverlayExitMs
                compact -> Motion.OverlayEnterMs
                else -> Motion.StandardMs
            }
            // **Easing follows direction.** One curve for both was a rule violation the system
            // states in its own tokens: "Entering or responding to input uses EaseOut; leaving uses
            // EaseIn." A panel that leaves on an ease-out curve hangs at the end of its own exit —
            // slowest exactly where it should already be gone.
            //
            // `flex-basis` rides the same clock as `opacity`/`transform` on the sidecar: a pane
            // whose box resizes on one curve while its content fades on another reads as two
            // animations rather than one panel arriving. It is the property flex layout actually
            // watches for this element's sizing; `width` transitions alongside it only because
            // some future reader of this box's rendered width should not see it lag.
            val animatedProperties = if (compact) {
                Motion.SurfaceProperties
            } else {
                Motion.SurfaceProperties + arrayOf("flex-basis", "width")
            }
            base.transition(
                *animatedProperties,
                durationMs = duration,
                easing = when {
                    phase == EditorUtilityPresencePhase.EXITING -> Motion.EaseIn
                    // The drawer curve, because this is a drawer. `LSheetMotion` uses it for every
                    // edge-anchored surface in the product and the assist sheet is one.
                    compact -> Motion.EaseDrawer
                    else -> Motion.EaseEntrance
                },
            )
        }
    }

    when (shell) {
        // **One `Div`, not two.** `MEASURING` and `SIDECAR` used to be separate `when` arms, each
        // its own `Div(...)` call — two different composable call sites, which is two different
        // slots in Compose's slot table. The instant `shell` flipped from one to the other (once a
        // remeasure settles), Compose disposed one node and composed a fresh one in its place: a
        // brand-new element's first paint has no prior frame on record for a CSS transition to
        // interpolate from, no matter what `transition` it declares. Confirmed on the real export
        // with Chrome DevTools Protocol's `Animation` domain — zero `Animation.animationStarted`
        // events fired for the whole open sequence, where a trivial control element and the
        // picker popover on the same page both fired one apiece.
        //
        // Sharing this one `Div` between the two shells is what
        // [EditorWorkspaceState.restartMeasurement]'s own contract already asks for: it restarts
        // *measurement*, not the *pane* — `EditorUtilityPane`'s own `LaunchedEffect`s already key
        // on `shell` and `row?.key?.id`, never on identity, and the panel's data-fetching effects
        // are explicitly written to survive a measuring pass ("a hidden second copy of this whole
        // pane", not a fresh one). This box was the one place that contract was not honoured: the
        // *element* itself still forgot who it was, even though nothing about it needed to.
        EditorUtilityShell.MEASURING, EditorUtilityShell.SIDECAR -> {
            val measuring = shell == EditorUtilityShell.MEASURING
            Div(
                EditorUtilitySidecarStyle.toModifier()
                    .surfaceAttributes(if (measuring) "measuring" else "sidecar")
                    .then(
                        if (measuring) {
                            // **Fixed at the settled width, never presence-driven.** This pass
                            // exists to measure the *editor's* remaining space against this pane's
                            // resting width — the same fixed width `EditorUtilityMeasuringStyle`
                            // used to bake into its own class. Reading `entering`/`flex-basis` off
                            // presence here would measure the editor as if the pane were still
                            // collapsed at `PENDING_ENTER`'s 0px, and the editor would then jump
                            // once the real width applied — the exact defect `abandonMeasurement`'s
                            // own comment warns a wrong measurement produces.
                            Modifier.styleModifier {
                                property("flex", "0 0 $EDITOR_UTILITY_TRACK")
                                property("width", EDITOR_UTILITY_TRACK)
                                property("visibility", "hidden")
                                property("pointer-events", "none")
                            }.noTransition()
                        } else {
                            Modifier.visibleMotion(compact = false)
                        },
                    )
                    .toAttrs {
                        if (measuring) {
                            attr("aria-hidden", "true")
                            attr("inert", "")
                        } else {
                            attr("role", "complementary")
                            if (labelledBy != null) {
                                attr("aria-labelledby", labelledBy)
                            } else {
                                attr("aria-label", utilityLabel)
                            }
                            if (dormant) {
                                attr("aria-hidden", "true")
                                attr("inert", "")
                            }
                        }
                    },
            ) {
                content()
            }
        }

        EditorUtilityShell.SHEET -> {
            val scrimBase = EditorUtilityScrimStyle.toModifier()
                .styleModifier {
                    val visible = phase == EditorUtilityPresencePhase.ENTERING ||
                        phase == EditorUtilityPresencePhase.PRESENT
                    property("opacity", if (visible) "1" else "0")
                    if (dormant) property("pointer-events", "none")
                }
            val scrimModifier = if (instant) {
                scrimBase.noTransition()
            } else {
                // The scrim shares the panel's clock and its curve. Two elements leaving together
                // on different curves read as two events.
                scrimBase.transition(
                    "opacity",
                    durationMs = if (phase == EditorUtilityPresencePhase.EXITING) {
                        Motion.OverlayExitMs
                    } else {
                        Motion.OverlayEnterMs
                    },
                    easing = if (phase == EditorUtilityPresencePhase.EXITING) {
                        Motion.EaseIn
                    } else {
                        Motion.EaseEntrance
                    },
                )
            }
            Div(
                scrimModifier
                    .toAttrs {
                        attr("data-editor-scrim", "true")
                        attr("aria-hidden", "true")
                        if (dormant) attr("inert", "")
                        onClick { onDismiss() }
                    },
            )
            Div(
                EditorUtilitySheetStyle.toModifier()
                    .surfaceAttributes("sheet")
                    .visibleMotion(compact = true)
                    .toAttrs {
                        attr("role", "dialog")
                        attr("aria-modal", "true")
                        if (labelledBy != null) {
                            attr("aria-labelledby", labelledBy)
                        } else {
                            attr("aria-label", utilityLabel)
                        }
                        attr("tabindex", "-1")
                        if (dormant) {
                            attr("aria-hidden", "true")
                            attr("inert", "")
                        }
                    },
            ) {
                content()
            }
        }
    }
}

internal data class EditorSuggestionInsertion(
    val keyId: String,
    val pluralCategory: PluralCategory?,
    val unsafe: Boolean,
)

internal data class EditorSuggestionInsertionDisposition(
    val closeUtility: Boolean,
    val focusTarget: Boolean,
)

internal fun suggestionInsertionDisposition(
    shell: EditorUtilityShell,
): EditorSuggestionInsertionDisposition = EditorSuggestionInsertionDisposition(
    closeUtility = shell == EditorUtilityShell.SHEET,
    focusTarget = true,
)

internal data class EditorUtilityShellSemantics(
    val role: String?,
    val ariaModal: Boolean,
    val ariaHidden: Boolean,
    val inert: Boolean,
    val scrim: Boolean,
)

internal fun utilityShellSemantics(shell: EditorUtilityShell): EditorUtilityShellSemantics = when (shell) {
    EditorUtilityShell.MEASURING -> EditorUtilityShellSemantics(
        role = null,
        ariaModal = false,
        ariaHidden = true,
        inert = true,
        scrim = false,
    )
    EditorUtilityShell.SIDECAR -> EditorUtilityShellSemantics(
        role = "complementary",
        ariaModal = false,
        ariaHidden = false,
        inert = false,
        scrim = false,
    )
    EditorUtilityShell.SHEET -> EditorUtilityShellSemantics(
        role = "dialog",
        ariaModal = true,
        ariaHidden = false,
        inert = false,
        scrim = true,
    )
}

internal fun panelLoadRequest(surface: EditorUtilitySurface): String? = when (surface) {
    EditorUtilitySurface.DISCUSS -> "comments"
    EditorUtilitySurface.ACTIVITY -> "history"
    EditorUtilitySurface.NONE,
    EditorUtilitySurface.ASSIST,
    EditorUtilitySurface.DETAILS,
    -> null
}

internal fun notifySuggestionInsertion(
    insertion: EditorSuggestionInsertion?,
    callback: (EditorSuggestionInsertion) -> Unit,
) {
    insertion?.let(callback)
}
